import type { AdminOrderDetailDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { CaptureActions } from './CaptureActions';
import { SectionTitle } from './InfoBlocks';
import { PaymentSummary } from './PaymentSummary';
import { PayoutSummary } from './PayoutSummary';
import { RefundAction } from './RefundAction';
import { PaymentCaptureStory } from '../../../components/capture';

/**
 * Платёж tab of the order detail drawer.
 *
 * A composer, nothing else — each block is its own component so the rules
 * that matter can be unit-tested without a drawer around them:
 *
 *   - `PaymentSummary`  — status chip, the TWO distinct money figures, the
 *     hold clock, card + settlement timestamps (AUT-219).
 *   - `PaymentCaptureStory` — what has already been TRIED against the hold and
 *     what happens next (AUT-309). Read-only and ungated: it is the difference
 *     between «холд» meaning "fine", "retrying" and "abandoned", and an operator
 *     without `process_payouts` still has to be able to tell those apart.
 *   - `CaptureActions`  — manual capture / reconcile, status-scoped and
 *     `process_payouts`-gated (AUT-223).
 *   - `RefundAction`    — the order-initiated refund (AUT-129).
 *   - `PayoutSummary`   — read-only; the retry lives on `/admin/payouts`.
 *
 * Each of payment / payout renders an explicit empty-state when its summary
 * is `null`, which is the common pre-completion case: no payment row until the
 * customer pays, no payout row until the capture creates one.
 *
 * This tab exists because before it, answering "did the money actually move"
 * meant SSH-ing to the box and querying the payment table by hand.
 *
 * The tab itself is visible to anyone who can open an order; only the money
 * ACTIONS gate on `process_payouts`. The read data is already sanitised
 * server-side (card last-4 only — architect lock AUT-112).
 */
const Empty = ({ message }: { message: string }): ReactElement => (
  <Typography variant="body2" color="text.secondary">
    {message}
  </Typography>
);

export const PaymentTab = ({ order }: { order: AdminOrderDetailDto }): ReactElement => {
  const { t } = useTranslation();
  const { payment, payout } = order;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <SectionTitle>{t('orders.detail.sections.payment')}</SectionTitle>
        {payment ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            <PaymentSummary payment={payment} />
            <PaymentCaptureStory capture={payment.capture} />
          </Box>
        ) : (
          <Empty message={t('orders.detail.payment.empty')} />
        )}
      </Box>

      {payment ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <CaptureActions payment={payment} />
          <RefundAction order={order} payment={payment} />
        </Box>
      ) : null}

      <Box>
        <SectionTitle>{t('orders.detail.sections.payout')}</SectionTitle>
        {payout ? (
          <PayoutSummary payout={payout} />
        ) : (
          <Empty message={t('orders.detail.payout.empty')} />
        )}
      </Box>
    </Box>
  );
};
